import {patternOrValue} from "./patternOrValue";
import {calcValueOfStringWithVar} from "../../pages/customer/processing/DetailProcessing/Canvas/CanvasCalcPositions/calcValueOfStringWithVar";

const findVariableByName = (list, name) => {
    if (!Array.isArray(list) || name == null || name === "") {
        return null;
    }
    const exact = list.find((row) => row?.name === name);
    if (exact) {
        return exact;
    }
    const upper = String(name).toUpperCase();
    return list.find((row) => String(row?.name ?? "").toUpperCase() === upper) ?? null;
};

/** L / W / H и переменные детали с value для подстановки в формулы отверстий. */
export const buildEffectiveDetailVariables = (detailVariables, detailData) => {
    const list = Array.isArray(detailVariables) ? detailVariables.map((row) => ({...row})) : [];

    const upsert = (name, value) => {
        if (value == null || value === "") {
            return;
        }
        const numeric = Number(value);
        if (!Number.isFinite(numeric)) {
            return;
        }
        const existing = findVariableByName(list, name);
        if (existing) {
            if (existing.value == null || existing.value === "") {
                existing.value = numeric;
            }
            return;
        }
        list.push({name, value: numeric});
    };

    upsert("L", detailData?.length);
    upsert("W", detailData?.width);
    upsert("H", detailData?.height);

    return list;
};

/** Текст координаты центра отверстия: pattern из center.variables или число с бэка. */
export const holeCenterCoordDisplay = (hole, axis) => {
    const raw = patternOrValue(hole?.center?.variables, axis, hole?.center?.[axis]);
    if (raw == null || raw === "") {
        return "";
    }
    return String(raw);
};

/** Текст радиуса / глубины: pattern из hole.variables или поле на DTO. */
export const holeScalarDisplay = (hole, variableName, fieldName) => {
    const raw = patternOrValue(hole?.variables, variableName, hole?.[fieldName]);
    if (raw == null || raw === "") {
        return "";
    }
    return String(raw);
};

const parsePlainNumber = (value) => {
    if (value == null || value === "") {
        return null;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    const str = String(value).trim().replace(",", ".");
    if (str === "") {
        return null;
    }
    const num = Number(str);
    return Number.isFinite(num) ? num : null;
};

const numericFromVariableRow = (row) => {
    if (!row) {
        return null;
    }
    const fromValue = parsePlainNumber(row.value);
    if (fromValue != null) {
        return fromValue;
    }
    return null;
};

/** Число для canvas: value на DTO, формула + переменные детали или литерал. */
export const tryResolveHoleExpression = (expression, detailVariables, localVariables = null) => {
    const plain = parsePlainNumber(expression);
    if (plain != null) {
        return plain;
    }
    const str = String(expression ?? "").trim();
    if (str === "") {
        return null;
    }

    const localRow = findVariableByName(localVariables, str);
    const fromLocal = numericFromVariableRow(localRow);
    if (fromLocal != null) {
        return fromLocal;
    }

    try {
        const resolved = calcValueOfStringWithVar(detailVariables, str);
        return Number.isFinite(resolved) ? resolved : null;
    } catch {
        return null;
    }
};

const resolveCenterAxis = (hole, axis, detailVariables) => {
    const localVars = hole?.center?.variables;
    const localRow = findVariableByName(localVars, axis);
    const fromLocalValue = numericFromVariableRow(localRow);
    if (fromLocalValue != null) {
        return fromLocalValue;
    }

    const display = holeCenterCoordDisplay(hole, axis);
    const fromDisplay = tryResolveHoleExpression(display, detailVariables, localVars);
    if (fromDisplay != null) {
        return fromDisplay;
    }

    return parsePlainNumber(hole?.center?.[axis]);
};

const resolveHoleScalar = (hole, variableName, fieldName, detailVariables) => {
    const localRow = findVariableByName(hole?.variables, variableName);
    const fromLocal = numericFromVariableRow(localRow);
    if (fromLocal != null) {
        return fromLocal;
    }

    const display = holeScalarDisplay(hole, variableName, fieldName);
    return tryResolveHoleExpression(display, detailVariables, hole?.variables)
        ?? parsePlainNumber(hole?.[fieldName]);
};

/**
 * Копия отверстия с числовыми center / radius / depth для 2D/3D-вида.
 * null — если координаты нельзя вычислить.
 */
export const resolveHoleForScene = (hole, detailVariables, detailData = null) => {
    if (!hole?.center) {
        return null;
    }
    const effectiveVars = buildEffectiveDetailVariables(detailVariables, detailData);

    const x = resolveCenterAxis(hole, "x", effectiveVars);
    const y = resolveCenterAxis(hole, "y", effectiveVars);
    const zRaw = holeCenterCoordDisplay(hole, "z");
    const z = tryResolveHoleExpression(zRaw, effectiveVars, hole?.center?.variables)
        ?? parsePlainNumber(hole?.center?.z)
        ?? 0;

    const radius = resolveHoleScalar(hole, "RADIUS", "radius", effectiveVars);
    const depth = resolveHoleScalar(hole, "DEPTH", "depth", effectiveVars);

    if (x == null || y == null || radius == null) {
        return null;
    }

    return {
        ...hole,
        radius,
        depth: depth ?? hole.depth,
        center: {
            ...hole.center,
            x,
            y,
            z,
        },
    };
};

export const mapHolesForScene = (holes, detailVariables, detailData = null) => {
    if (!Array.isArray(holes)) {
        return [];
    }
    const resolved = holes
        .map((hole) => resolveHoleForScene(hole, detailVariables, detailData))
        .filter(Boolean);
    if (resolved.length > 0) {
        return resolved;
    }
    return holes.filter((hole) => {
        const x = parsePlainNumber(hole?.center?.x);
        const y = parsePlainNumber(hole?.center?.y);
        const r = parsePlainNumber(hole?.radius);
        return x != null && y != null && r != null;
    });
};
